import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaUsers, FaHistory, FaBookOpen, FaHandshake, FaBullseye, FaBuilding, FaGlobe, FaGraduationCap, FaBriefcase, FaClock, FaEuroSign, FaMapMarkerAlt, FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaFemale, FaCalendarAlt, FaArrowRight, FaAward, FaLightbulb } from 'react-icons/fa';
import { Cloudinary } from '@cloudinary/url-gen';
import SEO from '../../components/SEO';
import './about.css';
import './dark-mode.css';
import './image-fallback.css';
import Typing from 'react-typing-effect';

const About = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const isArabic = language === 'ar';
  
  // Détection des appareils mobiles pour réduire les animations
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Vérification initiale
    checkMobile();
    
    // Ajout d'un écouteur d'événement pour le redimensionnement
    window.addEventListener('resize', checkMobile);
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // State pour la catégorie d'équipe active
  const [activeTeamCategory, setActiveTeamCategory] = useState('all');
  // State pour la catégorie de partenaires active
  const [activePartnerCategory, setActivePartnerCategory] = useState('institutional');

  // State pour l'index du membre d'équipe actif dans le carrousel
  const [activeTeamMemberIndex, setActiveTeamMemberIndex] = useState(0);
  
  // State pour forcer le rafraîchissement des données de traduction
  const [refreshKey, setRefreshKey] = useState(0);

  // State pour le texte typé
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Configuration Cloudinary
  const cld = new Cloudinary({
    cloud: {
      cloudName: 'dozvy3xru'
    }
  });

  // URL des vidéos
  const videoApropos = cld.video('swoxtuor7ah47g1ufbbg.mp4');
  
  // Fonction pour générer l'URL Cloudinary pour les images d'équipe
  const getTeamMemberImageUrl = (member, index) => {
    // Tableau des IDs Cloudinary pour les images d'équipe
    const teamImageIds = [
      'gkvzpzyjdulfszyqjg3j', 'xxjq9z95hkidytosvxzh', 'stdh8vnrrcpndfjxfkpz',
      'lxj5tiw4vkovjgdmkfjz', 'krpbrwveaf6to0kzkhwx', 'ofyamauco6ghqwpjoxsr',
      'yva2xuau4f6a4bzc7lyh', 'wyyiduqwxdfaxaeorpsx', 'er3gdsup8pswzttrajg5',
      'vyz0gnknoz0kjyxmgmts', 'bcg0cq1sr1kc10dzdljo', 'ocg9oqom7dzfqvgowlhg',
      'dkgmbswsp4zlppxcdq1v', 'f80l3jvnmffn6ogb9hc5', 'nkd2tnl8lt4kvduxh0hk',
      'zkclt9z57dcy8obyhite', 'sob7qnqfixo0facy76wf', 'mmxcgifgmdrseaephsgu',
      'ifj6koubhlbgpxgtjouy', 'vpcd3ph2diu1tnkuhjbt', 'ctq4zwhtgqeptqich3ne',
      'ozedf5lv2i71akxp3otw', 'mwoolxjky4hlxdoymcmq', 'yvk0tpjksz3vgdmfueqd',
      'jjqekybsjol4sca73r7a','wezcmveqi2tcrgf3ju92'  
    ];
    
    // Mapping des noms de fichiers vers les IDs Cloudinary
    const imageMapping = {
      'ouafa.jpg': 'wyyiduqwxdfaxaeorpsx',
      'asma.jpg': 'mwoolxjky4hlxdoymcmq',
      'zeineb.jpg': 'stdh8vnrrcpndfjxfkpz',
      'ahmed.jpg': 'ofyamauco6ghqwpjoxsr',
      'rania.jpg': 'u4boq7wivwz42kb9uslc',
      'nour.jpg': 'uz1d8bgs0mkqw0eysj2c',
      'ahmedbl.jpg': 'gbbtybr8zobqdelaj6qd',
      'ichraf.jpg': 'ctq4zwhtgqeptqich3ne',
      'nidhal.jpg': 'o5ksogkclrrzxvlzlqxj',
      'taher.jpg': 'lxj5tiw4vkovjgdmkfjz',
      'emna.jpg': 'f80l3jvnmffn6ogb9hc5',
      'rim.jpg': 'er3gdsup8pswzttrajg5',
      'salah.jpg': 'cxkmjpsmznyn3ysmin0c',
      'feryel.jpg': 'vyz0gnknoz0kjyxmgmts',
      'imen.jpg': 'vpcd3ph2diu1tnkuhjbt',
      'mariem.jpg': 'mmxcgifgmdrseaephsgu',
      'mohamed.jpg': 'krpbrwveaf6to0kzkhwx',
      'oumayma.jpg': 'dkgmbswsp4zlppxcdq1v',
      'majdi.jpg': 'ifj6koubhlbgpxgtjouy',
      'fares.jpg': 'uzjnyqyipm5rr9pixuid',
      'anaguim.jpg': 'gkvzpzyjdulfszyqjg3j',
      'xavier.jpg': 'xxjq9z95hkidytosvxzh',
      'maysson.jpg': 'orqsxep2ut22jzfnqqj9',
      'mohameds.jpg': 'nkd2tnl8lt4kvduxh0hk',
      'amine.jpg': 'ezb5l1jrbfz71auenlsm',
      'sameh.jpg': 'ocg9oqom7dzfqvgowlhg',  
      'zied.jpg': 'yva2xuau4f6a4bzc7lyh',  
      'baher.jpg': 'jjqekybsjol4sca73r7a',
      'fatma.jpg': 'wezcmveqi2tcrgf3ju92'  
    };
    
    // Si le membre a un chemin d'image défini
    if (member && member.image) {
      // Extraire le nom du fichier du chemin complet
      const fileName = member.image.split('/').pop();
      
      // Vérifier si le nom du fichier est dans le mapping
      if (imageMapping[fileName]) {
        // Utiliser l'ID Cloudinary correspondant avec qualité réduite sur mobile
        const quality = isMobile ? 'q_auto,f_auto' : 'q_100,f_auto';
        const width = isMobile ? 'w_300' : 'w_400';
        return `https://res.cloudinary.com/dozvy3xru/image/upload/c_scale,${width},${quality}/équipe/${imageMapping[fileName]}`;
      }
      // Sinon, conserver le chemin d'origine
      return member.image;
    }
    
    // Si aucune image n'est définie, utiliser un ID basé sur l'index avec des transformations
    const imageId = teamImageIds[index % teamImageIds.length];
    return `https://res.cloudinary.com/dozvy3xru/image/upload/c_fill,g_face,w_400,h_500,q_auto,f_auto/équipe/${imageId}`;
  };
  

  // Optimisation des animations pour améliorer les performances sur mobile
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: isMobile ? 0 : 0.05,
        delayChildren: isMobile ? 0 : 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: isMobile ? 1 : 0, y: isMobile ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isMobile ? 0 : 0.3
      }
    }
  };
  
  const fadeInVariants = {
    hidden: { opacity: isMobile ? 1 : 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        duration: isMobile ? 0 : 0.5 
      } 
    }
  };
  
  const scaleUpVariants = {
    hidden: { opacity: isMobile ? 1 : 0, scale: isMobile ? 1 : 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      transition: { 
        duration: isMobile ? 0 : 0.3 
      } 
    }
  };

  const slideInLeftVariants = {
    hidden: { opacity: isMobile ? 1 : 0, x: isMobile ? 0 : -20 },
    visible: { 
      opacity: 1, 
      x: 0, 
      transition: { 
        duration: isMobile ? 0 : 0.3 
      } 
    }
  };
  
  const slideInRightVariants = {
    hidden: { opacity: isMobile ? 1 : 0, x: isMobile ? 0 : 20 },
    visible: { 
      opacity: 1, 
      x: 0, 
      transition: { 
        duration: isMobile ? 0 : 0.3 
      } 
    }
  };
  
  const popUpVariants = {
    hidden: { opacity: isMobile ? 1 : 0, scale: isMobile ? 1 : 0.95 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: isMobile ? 0 : i * 0.05,
        duration: isMobile ? 0 : 0.2,
        ease: "easeOut"
      }
    })
  };

  const descriptionVariants = {
    hidden: { opacity: isMobile ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: isMobile ? 0 : 0.5
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: isMobile ? 1 : 0, y: isMobile ? 0 : 10 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: isMobile ? 0 : 0.3 
      } 
    }
  };

  // Hooks pour l'animation au scroll
  const [heroRef, heroInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [iecdRef, iecdInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [dncRef, dncInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [missionRef, missionInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [historyRef, historyInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [teamRef, teamInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [partnersRef, partnersInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [valuesRef, valuesInView] = useInView({ threshold: 0.2, triggerOnce: true });
  
  // Effet pour forcer le rechargement des traductions lors du changement de catégorie
  useEffect(() => {
    // Forcer le rechargement des traductions
    i18n.reloadResources(i18n.languages, ['about']).then(() => {
      // Réinitialiser l'index du membre actif
      setActiveTeamMemberIndex(0);
    });
  }, [i18n, activeTeamCategory]);

  // Effet de typewriter
  const handleTyping = (text) => {
    setTypedText('');
    setIsTyping(true);

    const typingInterval = setInterval(() => {
      if (typedText.length < text.length) {
        setTypedText(prev => prev + text[typedText.length]);
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, 50); // Vitesse de typage

    return () => clearInterval(typingInterval);
  };

  // Fonction pour changer le membre d'équipe actif
  const nextTeamMember = () => {
    const filteredMembers = getFilteredTeamMembers();
    setActiveTeamMemberIndex((prevIndex) => 
      prevIndex === filteredMembers.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTeamMember = () => {
    const filteredMembers = getFilteredTeamMembers();
    setActiveTeamMemberIndex((prevIndex) => 
      prevIndex === 0 ? filteredMembers.length - 1 : prevIndex - 1
    );
  };

  // Effet pour faire défiler automatiquement les membres de l'équipe
  // Désactivé sur mobile pour améliorer les performances
  useEffect(() => {
    if (!isMobile) {
      const interval = setInterval(() => {
        nextTeamMember();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [activeTeamCategory, activeTeamMemberIndex, isMobile]);

  // Fonction pour obtenir les membres de l'équipe filtrés par catégorie
  const getFilteredTeamMembers = () => {
    try {
      const teamMembers = t('about.team.members', { returnObjects: true }) || [];
      if (!Array.isArray(teamMembers)) {
        console.error('teamMembers is not an array:', teamMembers);
        return [];
      }
      if (activeTeamCategory === 'all') {
        return teamMembers;
      }
      return teamMembers.filter(member => member && member.category === activeTeamCategory);
    } catch (error) {
      console.error('Error in getFilteredTeamMembers:', error);
      return [];
    }
  };

  // Fonction pour obtenir les partenaires filtrés par catégorie
  const getFilteredPartners = () => {
    // Liste simple des partenaires avec les images disponibles
    const allPartners = [
      {
        id: 1,
        name: "Agence Française de Développement",
        category: "institutional",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238275/Logo_France_AFD_2_ijm3sp.png",
        description: t('about.partners.partnersList.afd')
      },
      {
        id: 2,
        name: "Ministère de l'Emploi - Tunisie",
        category: "institutional",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238328/logo_MEFP_w5nm4y.png",
        description: t('about.partners.partnersList.mefp')
      },
      {
        id: 3,
        name: "ANETI",
        category: "institutional",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238282/logo_ANETI_3_-1_etxyod.png",
        description: t('about.partners.partnersList.aneti')
      },
      {
        id: 4,
        name: "ATFP",
        category: "institutional",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238335/logo_atfp_vectorielle_trtors.png",
        description: t('about.partners.partnersList.atfp')
      },
      {
        id: 5,
        name: "Rose Blanche",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238304/LOGO-LRB_vjdpxk.png",
        description: t('about.partners.partnersList.lrb')
      },
      {
        id: 6,
        name: "Sleh Ayed",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185795/partenaire/Logos%20Entreprises/odehhwnxcppciatunqki.png",
        description: t('about.partners.partnersList.slehAyed')
      },
      {
        id: 7,
        name: "Sancella",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185733/partenaire/Logos%20Entreprises/Sancella/mlzzyrcmiqkdgpjwhcvy.webp",
        description: t('about.partners.partnersList.sancella')
      },
      {
        id: 8,
        name: "Plasti-ka",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185733/partenaire/Logos%20Entreprises/Plasti-ka/fgeipy1bnzwqcoj8nydi.jpg",
        description: t('about.partners.partnersList.plastika')
      },
      {
        id: 9,
        name: "KMC",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/KMC/es40wqevf2huilrxltwg.png",
        description: t('about.partners.partnersList.kmc')
      },
      {
        id: 10,
        name: "Nakcha Restaurant",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/KMC/es40wqevf2huilrxltwg.png",
        description: t('about.partners.partnersList.nakcha')
      },
      {
        id: 11,
        name: "Kids Land Kairouan",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/Kids%20land%20kairouan/xqykrcbwzdolqmf664uu.jpg",
        description: t('about.partners.partnersList.kidsLand')
      },
      {
        id: 12,
        name: "Hôtel Continental",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/H%C3%B4tel%20Continental/g1iv55i9ubzuuh5jq2qq.png",
        description: t('about.partners.partnersList.continental')
      },
      {
        id: 13,
        name: "Gym Club",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/Gym%20club/hbsxdh91jf0qkva5xq8u.jpg",
        description: t('about.partners.partnersList.gymClub')
      },
      {
        id: 14,
        name: "Eurocycles",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/Eurocycles/iwadygausye4wd54ya6e.jpg",
        description: t('about.partners.partnersList.eurocycles')
      },
      {
        id: 15,
        name: "GFI University",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/GFI%20university/o5hyqsuurrwviyy5qs2i.png",
        description: t('about.partners.partnersList.gfi')
      },
      {
        id: 16,
        name: "Billionnaire",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/Billionnaire/j2qbqgz2v6molihczun1.png",
        description: t('about.partners.partnersList.billionnaire')
      },
      {
        id: 17,
        name: "ETS Mellouli",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/ETS%20Mellouli/vtcskw048nnw9z4sv0dx.png",
        description: t('about.partners.partnersList.mellouli')
      },
      {
        id: 18,
        name: "Essoukri",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185732/partenaire/Logos%20Entreprises/Essoukri/ytagg7fjo7jzutawqaai.jpg",
        description: t('about.partners.partnersList.essoukri')
      },
      {
        id: 19,
        name: "AMT",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185731/partenaire/Logos%20Entreprises/AMT/gcqv1ryfhpof0n7cajot.jpg",
        description: t('about.partners.partnersList.amt')
      },
      {
        id: 20,
        name: "El Mouradi",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185731/partenaire/Logos%20Entreprises/El%20Mouradi/r6qxxuctkhfcyduedypw.gif",
        description: t('about.partners.partnersList.elMouradi')
      },
      {
        id: 21,
        name: "Decathlon",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745185731/partenaire/Logos%20Entreprises/Decathlon/azaj4mbxhcqegygz6i23.png",
        description: t('about.partners.partnersList.decathlon')
      },
      {
        id: 22,
        name: "MG",
        category: "enterprises",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238318/Logo_MG_hngyq9.jpg",
        description: t('about.partners.partnersList.mg')
      },
      {
        id: 23,
        name: "RAFIKI",
        category: "ngos",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238318/partenaire/iqslukvrfex3zxuuqql9.jpg",
        description: t('about.partners.partnersList.rfiki')
      },
      {
        id: 24,
        name: "FTARTCHY",
        category: "ngos",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238318/partenaire/gh3jxluzbtoutpuaqmwb.jpg",
        description: t('about.partners.partnersList.ftartchy')
      },
      {
        id: 25,
        name: "AWLEDNA",
        category: "ngos",
        logo: "https://res.cloudinary.com/dozvy3xru/image/upload/v1745238318/partenaire/uzr7ld9zd7or0wlbb15b.jpg",
        description: t('about.partners.partnersList.awledna')
      }
    ];
    
    // Filtrer par catégorie
    if (activePartnerCategory === 'all') {
      return allPartners;
    }
    
    return allPartners.filter(partner => partner.category === activePartnerCategory);
  };

  // Fonction pour obtenir l'icône sociale
  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'facebook':
        return <FaFacebook />;
      case 'twitter':
        return <FaTwitter />;
      case 'linkedin':
        return <FaLinkedin />;
      case 'instagram':
        return <FaInstagram />;
      default:
        return null;
    }
  };

  // Fonction pour obtenir l'icône de valeur
  const getValueIcon = (icon) => {
    switch (icon) {
      case 'FaGlobe':
        return <FaGlobe />;
      case 'FaUsers':
        return <FaUsers />;
      case 'FaBriefcase':
        return <FaBriefcase />;
      case 'FaHandshake':
        return <FaHandshake />;
      case 'FaBullseye':
        return <FaBullseye />;
      case 'FaBuilding':
        return <FaBuilding />;
      case 'FaGraduationCap':
        return <FaGraduationCap />;
      case 'FaClock':
        return <FaClock />;
      case 'FaEuroSign':
        return <FaEuroSign />;
      case 'FaMapMarkerAlt':
        return <FaMapMarkerAlt />;
      case 'FaAward':
        return <FaAward />;
      case 'FaLightbulb':
        return <FaLightbulb />;
      default:
        return null;
    }
  };

  return (
    <div className={`about-page ${isArabic ? 'rtl' : ''}`}>
      <SEO 
        title={isArabic ? 'عن المنظومة الوطنية للفرصة الجديدة - DNC تونس' : 'À propos du Dispositif National Nouvelle Chance - DNC Tunisie'}
        description={isArabic ? 'تعرف على المنظومة الوطنية للفرصة الجديدة، مهمتنا، رؤيتنا وأهدافنا لدعم الشباب المنقطعين عن الدراسة في تونس' : 'Découvrez le Dispositif National Nouvelle Chance, notre mission, notre vision et nos objectifs pour soutenir les jeunes déscolarisés en Tunisie'}
        url="/about"
        type="website"
      />
      {/* Hero Section */}
      
        <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
        <video
          autoPlay
          muted
          loop
          className="absolute w-full h-full object-cover"
        >
          <source src={videoApropos.toURL()} type="video/mp4" />
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{t('about.hero.title')}</h1>
          <p className="text-xl md:text-2xl text-white">{t('about.hero.subtitle')}</p>
        </div>
        </section>
       

      {/* DNC Section */}
      <motion.section 
        ref={dncRef}
        initial="hidden"
        animate={dncInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="dnc-section section"
      >
        <div className="container">
          
                     <div className="about-dnc-content">
                       <div className="about-dnc-text">
                         <motion.h3
                           initial={{ opacity: 0 }}
                           whileInView={{ opacity: 1 }}
                           transition={{ duration: 0.8 }}
                         >
                           <i className="fas fa-lightbulb title-icon"></i>
                           {language === 'ar' 
                             ? 'مشروع تجريبي في سوسة والقيروان لدعم الشباب غير المنخرط وتعزيز قابلية التشغيل'
                             : 'Un projet pilote à Sousse et à Kairouan pour soutenir les jeunes sans diplômes et sans qualification et renforcer leur employabilité'}
                         </motion.h3>
                         
                         <div className="typing-animation-container">
                           <motion.div
                             className="typing-text"
                           >
                              {language === 'ar' 
                                ? <p>{"المنظومة الوطنية للفرصة الجديدة هي مشروع تجريبي في سوسة والقيروان يهدف إلى تعزيز فرص التشغيل وتمكين الشباب التونسي غير المنخرط في التعليم أو التدريب أو العمل (NEET) من خلال المرافقة والتوجيه والفرص العملية."}</p>
                                : <p>{"Le Dispositif National Nouvelle Chance est un projet pilote à Sousse et à Kairouan visant à renforcer l'employabilité et à autonomiser les jeunes tunisiens NEET (ni en études, ni en emploi, ni en formation) par l'accompagnement, le mentorat et des opportunités pratiques."}</p>}
                           </motion.div>
                         </div>
                         
                         <motion.div
                           className="about-dnc-features"
                           initial={{ opacity: 0, y: 20 }}
                           whileInView={{ opacity: 1, y: 0 }}
                           transition={{ duration: 0.8, delay: 0.5 }}
                         >
                           <div className="feature">
                             <div className="feature-icon">
                               <i className="fas fa-graduation-cap"></i>
                             </div>
                             <div className="feature-text">
                               <h4>{language === 'ar' ? 'بيداغوجيا متخصصة للشباب غير المنخرط' : 'Pédagogie spécialisée pour le public NEET (ni en études, ni en emploi, ni en formation)'}</h4>
                               <p>{language === 'ar' 
                                 ? 'منهجية تعليمية مكيفة للشباب غير المنخرط مصممة لتطوير قابلية التشغيل'
                                 : 'Pédagogie adaptée pour le public NEET (ni en études, ni en emploi, ni en formation) conçue pour développer l\'employabilité'}
                               </p>
                             </div>
                           </div>
                           
                           <div className="feature">
                             <div className="feature-icon">
                               <i className="fas fa-users"></i>
                             </div>
                             <div className="feature-text">
                               <h4>{language === 'ar' ? 'مرافقة شخصية' : 'Accompagnement personnalisé'}</h4>
                               <p>{language === 'ar' 
                                 ? 'مرافقة فردية لكل مشارك لتحقيق أهدافه'
                                 : 'Accompagnement individuel pour chaque participant afin d\'atteindre ses objectifs'}
                               </p>
                             </div>
                           </div>
                           
                           <div className="feature">
                             <div className="feature-icon">
                               <i className="fas fa-briefcase"></i>
                             </div>
                             <div className="feature-text">
                               <h4>{language === 'ar' ? 'فرص عملية' : 'Opportunités pratiques'}</h4>
                               <p>{language === 'ar' 
                                 ? 'تدريبات عملية وفرص مهنية مع شركائنا'
                                 : 'Stages pratiques et opportunités professionnelles avec nos partenaires'}
                               </p>
                             </div>
                           </div>
                         </motion.div>
                       </div>
                       
                       <motion.div 
                         className="about-dnc-image"
                         initial={{ opacity: 0, x: 50 }}
                         whileInView={{ opacity: 1, x: 0 }}
                         transition={{ duration: 0.8 }}
                       >
                         <img src={language === 'fr' ? '/image/logofr.png' : '/image/logoar.png'}  alt="Programme DNC" />
                       </motion.div>
                     </div>
                    
          <motion.div variants={slideInRightVariants} className="dnc-context">
            <h3>{t('about.dnc.context.title')}</h3>
            <div className="dnc-context-stats">
              <motion.div 
                className="context-stat-item"
                whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
              >
                <div className="stat-icon"><FaUsers /></div>
                <p>{t('about.dnc.context.unemployment')}</p>
              </motion.div>
              <motion.div 
                className="context-stat-item"
                whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
              >
                <div className="stat-icon"><FaGraduationCap /></div>
                <p>{t('about.dnc.context.neets')}</p>
              </motion.div>
              <motion.div 
                className="context-stat-item"
                whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
              >
                <div className="stat-icon"><FaFemale /></div>
                <p>{t('about.dnc.context.women')}</p>
              </motion.div>
              <motion.div 
                className="context-stat-item source-item"
                style={{ 
                  width: '100%', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontStyle: 'italic',
                  color: '#666',
                  boxShadow: 'none',
                  padding: '5px 15px'
                }}
              >
                <p>{t('about.dnc.context.source')}</p>
              </motion.div>
            </div>
          </motion.div>
          
          <motion.div 
            className="dnc-project-framework"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={{ 
              backgroundColor: "white", 
              borderRadius: "15px", 
              padding: "30px",
              marginTop: "30px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <div className="framework-background" style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `linear-gradient(135deg, rgba(255, 209, 102, 0.05) 0%, rgba(15, 76, 129, 0.05) 100%)`,
              zIndex: 0
            }}></div>
            
            <h3 style={{ 
              color: "#0f4c81", 
              borderBottom: "2px solid #ffd166", 
              paddingBottom: "10px", 
              display: "inline-block",
              position: "relative",
              zIndex: 1
            }}>
              {t('about.dnc.framework.title')}
            </h3>
            
            <div className="project-timeline" style={{ 
              position: "relative", 
              marginTop: "30px",
              zIndex: 1
            }}>
              {/* Ligne de frise horizontale */}
              <div style={{
                position: "absolute",
                top: "50px",
                left: 0,
                width: "100%",
                height: "4px",
                backgroundColor: "#ffd166",
                zIndex: 0
              }}></div>
              
              <motion.div 
                className={`timeline-step ${isMobile ? 'motion-reduce' : ''}`}
                initial={{ opacity: 0, y: isMobile ? 0 : 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: isMobile ? 0 : 0.2, duration: isMobile ? 0.2 : 0.3 }}
                whileHover={isMobile ? {} : { 
                  y: -3, 
                  boxShadow: '0 8px 15px rgba(15, 76, 129, 0.15)',
                  transition: { type: "spring", stiffness: 200 }
                }}
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
                  position: "relative",
                  zIndex: 2
                }}
              >
                <div className="step-number" style={{ 
                  backgroundColor: "#0f4c81", 
                  color: "white",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  marginRight: "15px",
                  boxShadow: "0 5px 15px rgba(15, 76, 129, 0.2)"
                }}>1</div>
                <div className="step-content">
                  <h4 style={{ color: "#0f4c81", marginBottom: "5px" }}>{t('about.dnc.framework.steps.step1.title')}</h4>
                  <p style={{ color: "#666", margin: 0 }}>{t('about.dnc.framework.steps.step1.date')}</p>
                </div>
              </motion.div>
                       
  
              <motion.div 
                className="timeline-step"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                  transition: { type: "spring", stiffness: 300 }
                }}
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
                  position: "relative",
                  zIndex: 2
                }}
              >
                <div className="step-number" style={{ 
                  backgroundColor: "#0f4c81", 
                  color: "white",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  marginRight: "15px",
                  boxShadow: "0 5px 15px rgba(15, 76, 129, 0.2)"
                }}>2</div>
                <div className="step-content">
                  <h4 style={{ color: "#0f4c81", marginBottom: "5px" }}>{t('about.dnc.framework.steps.step2.title')}</h4>
                  <p style={{ color: "#666", margin: 0 }}>{t('about.dnc.framework.steps.step2.date')}</p>
                </div>
              </motion.div>
              <motion.div 
                className="timeline-step"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                  transition: { type: "spring", stiffness: 300 }
                }}
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
                  position: "relative",
                  zIndex: 2
                }}
              >
                <div className="step-number" style={{ 
                  backgroundColor: "#0f4c81", 
                  color: "white",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  marginRight: "15px",
                  boxShadow: "0 5px 15px rgba(15, 76, 129, 0.2)"
                }}>3</div>
                <div className="step-content">
                  <h4 style={{ color: "#0f4c81", marginBottom: "5px" }}>
                    {language === 'ar' 
                      ? 'الإعلان الرسمي عن برنامج الفرصة الجديدة' 
                      : 'LANCEMENT OFFICIEL DU DNC '}
                  </h4>
                  <p style={{ color: "#666", margin: 0 }}>
                    {language === 'ar' 
                      ? 'يونيو 2024' 
                      : 'Juin 2024'}
                  </p>
                </div>
              </motion.div>
              <motion.div 
                className="timeline-step"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.4 }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                  transition: { type: "spring", stiffness: 300 }
                }}
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
                  position: "relative",
                  zIndex: 2
                }}
              >
                <div className="step-number" style={{ 
                  backgroundColor: "#0f4c81", 
                  color: "white",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  marginRight: "15px",
                  boxShadow: "0 5px 15px rgba(15, 76, 129, 0.2)"
                }}>4</div>
                <div className="step-content">
                  <h4 style={{ color: "#0f4c81", marginBottom: "5px" }}>{t('about.dnc.framework.steps.step3.title')}</h4>
                  <p style={{ color: "#666", margin: 0 }}>{t('about.dnc.framework.steps.step3.date')}</p>
                </div>
              </motion.div>
              
              <motion.div 
                className="timeline-step"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.4 }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                  transition: { type: "spring", stiffness: 300 }
                }}
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
                  position: "relative",
                  zIndex: 2
                }}
              >
                <div className="step-number" style={{ 
                  backgroundColor: "#0f4c81", 
                  color: "white",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  marginRight: "15px",
                  boxShadow: "0 5px 15px rgba(15, 76, 129, 0.2)"
                }}>5</div>
                <div className="step-content">
                  <h4 style={{ color: "#0f4c81", marginBottom: "5px" }}>{t('about.dnc.framework.steps.step4.title')}</h4>
                  <p style={{ color: "#666", margin: 0 }}>{t('about.dnc.framework.steps.step4.date')}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
          
          <motion.div 
            className={`dnc-details ${isMobile ? 'motion-reduce' : ''}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: isMobile ? 0.2 : 0.3 }}
            style={{ 
              backgroundColor: "white", 
              borderRadius: "15px", 
              padding: "30px",
              marginTop: "30px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)"
            }}
          >
            <h3 style={{ 
              color: "#0f4c81", 
              borderBottom: "2px solid #ffd166", 
              paddingBottom: "10px", 
              display: "inline-block" 
            }}>
              {t('about.dnc.details.title')}
            </h3>
            
            
            <motion.div 
              variants={slideInLeftVariants} 
              className="dnc-impact"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              style={{ 
                backgroundColor: "white", 
                borderRadius: "15px", 
                padding: "25px",
                marginTop: "30px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                borderLeft: "5px solid #0f4c81"
              }}
            >
              <h4 style={{ 
                color: "#0f4c81", 
                fontSize: "1.3rem", 
                marginBottom: "15px",
                borderBottom: "2px solid #ffd166",
                paddingBottom: "8px",
                display: "inline-block"
              }}>
                {t('about.dnc.details.impact.title')}
              </h4>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                style={{ 
                  fontSize: "1.05rem", 
                  lineHeight: "1.6", 
                  color: "#444",
                  padding: "10px 0",
                  borderLeft: "3px solid #ffd166",
                  paddingLeft: "15px",
                  margin: "15px 0",
                  backgroundColor: "rgba(255, 209, 102, 0.05)",
                  borderRadius: "0 8px 8px 0"
                }}
              >
                {t('about.dnc.details.impact.rate')}
              </motion.p>
              
               </motion.div>
          </motion.div>
          
          <motion.div 
            className={`dnc-summary ${isMobile ? 'motion-reduce' : ''}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: isMobile ? 0.2 : 0.3 }}
            style={{ 
              backgroundColor: "white", 
              borderRadius: "15px", 
              padding: "30px",
              marginTop: "30px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <div className="summary-background" style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `linear-gradient(135deg, rgba(255, 209, 102, 0.05) 0%, rgba(15, 76, 129, 0.05) 100%)`,
              zIndex: 0
            }}></div>
            
            <h3 style={{ 
              color: "#0f4c81", 
              borderBottom: "2px solid #ffd166", 
              paddingBottom: "10px", 
              display: "inline-block",
              position: "relative",
              zIndex: 1
            }}>
              {t('about.dnc.summary.title')}
            </h3>
            
            <div className="summary-grid" style={{ 
              position: "relative",
              zIndex: 1,
              marginTop: "25px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "20px"
            }}>
              
              
              <motion.div 
                className="summary-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                style={{ 
                  backgroundColor: "white",
                  borderRadius: "15px",
                  padding: "25px",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
                  border: "1px solid rgba(15, 76, 129, 0.1)",
                  transform: "translateZ(0)",
                  width: "100%"
                }}
              >
                <h4 style={{ 
                  color: "#0f4c81", 
                  marginBottom: "20px", 
                  fontSize: "1.3rem",
                  fontWeight: "600",
                  textAlign: "center"
                }}>
                  {t('about.dnc.summary.centers')}
                </h4>
                
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "30px",
                  marginBottom: "10px",
                  flexWrap: "wrap"
                }}>
                  {/* Carte DNC-Sousse */}
                  <motion.a 
                    href="/contact"
                    style={{
                      textDecoration: "none",
                      flex: "0 0 45%",
                      maxWidth: "400px",
                      backgroundColor: "white",
                      borderRadius: "10px",
                      padding: "20px",
                      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
                      border: "1px solid rgba(15, 76, 129, 0.2)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer"
                    }}
                    whileHover={{ 
                      y: -5, 
                      boxShadow: '0 10px 25px rgba(15, 76, 129, 0.2)',
                      transition: { type: "spring", stiffness: 300 }
                    }}
                  >
                    <motion.div 
                      style={{ 
                        backgroundColor: "#ffd166", 
                        color: "#0f4c81",
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "1.8rem",
                        marginBottom: "15px",
                        boxShadow: "0 5px 10px rgba(255, 209, 102, 0.3)"
                      }}
                      whileHover={{ 
                        rotate: 360,
                        transition: { duration: 0.5 }
                      }}
                    >
                      <FaBuilding />
                    </motion.div>
                    <h5 style={{ 
                      color: "#0f4c81", 
                      margin: "5px 0", 
                      fontSize: "1.3rem",
                      fontWeight: "600"
                    }}>
                      {language === 'ar' ? 'مركز سوسة' : 'DNC-Sousse'}
                    </h5>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "#444",
                      margin: "10px 0",
                      textAlign: "center"
                    }}>
                      {language === 'ar' 
                        ? 'عمارة حبيبة، نهج جوهرة الساحل - خزامة'
                        : 'Immeuble Habiba, Rue Jawharat Essahel - Khezama'}
                    </p>
                  </motion.a>
                  
                  {/* Carte DNC-Kairouan */}
                  <motion.a 
                    href="/contact"
                    style={{
                      textDecoration: "none",
                      flex: "0 0 45%",
                      maxWidth: "400px",
                      backgroundColor: "white",
                      borderRadius: "10px",
                      padding: "20px",
                      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
                      border: "1px solid rgba(15, 76, 129, 0.2)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer"
                    }}
                    whileHover={{ 
                      y: -5, 
                      boxShadow: '0 10px 25px rgba(15, 76, 129, 0.2)',
                      transition: { type: "spring", stiffness: 300 }
                    }}
                  >
                    <motion.div 
                      style={{ 
                        backgroundColor: "#ffd166", 
                        color: "#0f4c81",
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "1.8rem",
                        marginBottom: "15px",
                        boxShadow: "0 5px 10px rgba(255, 209, 102, 0.3)"
                      }}
                      whileHover={{ 
                        rotate: 360,
                        transition: { duration: 0.5 }
                      }}
                    >
                      <FaBuilding />
                    </motion.div>
                    <h5 style={{ 
                      color: "#0f4c81", 
                      margin: "5px 0", 
                      fontSize: "1.3rem",
                      fontWeight: "600"
                    }}>
                      {language === 'ar' ? 'مركز القيروان' : 'DNC-Kairouan'}
                    </h5>
                    <p style={{ 
                      fontSize: "1rem", 
                      color: "#444",
                      margin: "10px 0",
                      textAlign: "center"
                    }}>
                      {language === 'ar' 
                        ? 'نهج طارق ابن زياد - القيروان'
                        : 'Rue Tarek Ibn Zied - Kairouan'}
                    </p>
                  </motion.a>
                </div>
              </motion.div>
              
              </div>
          </motion.div>
        </div>
      </motion.section>
      
      {/* IECD Section */}
      <motion.section 
        initial="hidden"
        ref={iecdRef}
        animate={iecdInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="iecd-section section"
      >
        <div className="container">
          <motion.div 
            variants={itemVariants} 
            className="section-header"
            whileInView={{ 
              y: [10, 0], 
              opacity: [0, 1] 
            }}
            transition={{ duration: 0.8 }}
          >
            <div className="section-icon-container" style={{ backgroundColor: "#ffd166" }}>
              <FaGlobe className="section-icon" style={{ color: "#0f4c81" }} />
            </div>
            <h2 className="section-title" style={{ color: "#0f4c81" }}>{t('about.iecd.title')}</h2>
            <p className="section-subtitle">{t('about.iecd.subtitle')}</p>
          </motion.div>

          <motion.div 
            variants={containerVariants} 
            className="iecd-content-flex"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div 
              className="iecd-logo-container"
              variants={popUpVariants}
              whileHover={{ 
                scale: 1.05,
                boxShadow: '0 15px 30px rgba(15, 76, 129, 0.15)',
                transition: { duration: 0.3 }
              }}
            >
              <img src="/image/iecd.png" alt="Logo IECD TUNISIE" className="iecd-logo" />
            </motion.div>
            <motion.div 
              className="iecd-description"
              variants={slideInRightVariants}
            >
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {t('about.iecd.description')}
              </motion.p>
              
              <motion.div 
                className="iecd-stats"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <motion.div 
                  className="iecd-stat-item"
                  whileHover={{ 
                    y: -10, 
                    boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                    transition: { type: "spring", stiffness: 300 }
                  }}
                  style={{ backgroundColor: "white", borderBottom: "4px solid #0f4c81" }}
                >
                  <motion.div 
                    className="stat-icon"
                    whileHover={{ 
                      rotate: 360,
                      transition: { duration: 0.5 }
                    }}
                    style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                  >
                    <FaGlobe />
                  </motion.div>
                  <div className="stat-number" style={{ color: "#0f4c81" }}>18</div>
                  <div className="stat-label">{t('about.iecd.stats.countries')}</div>
                </motion.div>
                <motion.div 
                  className="iecd-stat-item"
                  whileHover={{ 
                    y: -10, 
                    boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                    transition: { type: "spring", stiffness: 300 }
                  }}
                  style={{ backgroundColor: "white", borderBottom: "4px solid #0f4c81" }}
                >
                  <motion.div 
                    className="stat-icon"
                    whileHover={{ 
                      rotate: 360,
                      transition: { duration: 0.5 }
                    }}
                    style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                  >
                    <FaUsers />
                  </motion.div>
                  <div className="stat-number" style={{ color: "#0f4c81", direction: isArabic ? 'rtl' : 'ltr' }}>{isArabic ? '28٬000' : '28 000'}</div>
                  <div className="stat-label">{t('about.iecd.stats.beneficiaries')}</div>
                </motion.div>
                <motion.div 
                  className="iecd-stat-item"
                  whileHover={{ 
                    y: -10, 
                    boxShadow: '0 10px 25px rgba(15, 76, 129, 0.15)',
                    transition: { type: "spring", stiffness: 300 }
                  }}
                  style={{ backgroundColor: "white", borderBottom: "4px solid #0f4c81" }}
                >
                  <motion.div 
                    className="stat-icon"
                    whileHover={{ 
                      rotate: 360,
                      transition: { duration: 0.5 }
                    }}
                    style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                  >
                    <FaBriefcase />
                  </motion.div>
                  <div className="stat-number" style={{ color: "#0f4c81" }}>73</div>
                  <div className="stat-label">{t('about.iecd.stats.projects')}</div>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="iecd-mission"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                style={{ borderLeft: "4px solid #ffd166", paddingLeft: "20px" }}
              >
                <h3 style={{ color: "#0f4c81" }}>{t('about.iecd.mission.title')}:</h3>
                <p>{t('about.iecd.mission.description')}</p>
              </motion.div>
              
              <motion.div 
                className="iecd-domains"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                style={{ borderLeft: "4px solid #ffd166", paddingLeft: "20px", marginTop: "20px" }}
              >
                <h3 style={{ color: "#0f4c81" }}>{t('about.iecd.domains.title')}</h3>
                <ul>
                  <motion.li 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9, duration: 0.3 }}
                  >
                    {t('about.iecd.domains.training')}
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.0, duration: 0.3 }}
                  >
                    {t('about.iecd.domains.entrepreneurship')}
                  </motion.li>
                  <motion.li 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1, duration: 0.3 }}
                  >
                    {t('about.iecd.domains.education')}
                  </motion.li>
                </ul>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div 
            variants={scaleUpVariants} 
            className="iecd-tunisia"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            style={{ backgroundColor: "white", borderRadius: "15px", boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)" }}
          >
            <h3 style={{ color: "#0f4c81", borderBottom: "2px solid #ffd166", paddingBottom: "10px", display: "inline-block" }}>{t('about.iecd.tunisia.title')}</h3>
            <div className="iecd-tunisia-stats">
              <motion.div 
                className="tunisia-stat-item"
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 20px rgba(15, 76, 129, 0.1)',
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                <motion.div 
                  className="stat-icon"
                  whileHover={{ 
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                  style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                >
                  <FaGlobe />
                </motion.div>
                <p>{t('about.iecd.tunisia.since')} <strong>2022</strong> {t('about.iecd.tunisia.inCountry')}</p>
              </motion.div>
              <motion.div 
                className="tunisia-stat-item"
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 20px rgba(15, 76, 129, 0.1)',
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                <motion.div 
                  className="stat-icon"
                  whileHover={{ 
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                  style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                >
                  <FaUsers />
                </motion.div>
                <p>~ <strong>450</strong> {t('about.iecd.tunisia.expectedBeneficiaries')}</p>
              </motion.div>
              <motion.div 
                className="tunisia-stat-item"
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 20px rgba(15, 76, 129, 0.1)',
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                <motion.div 
                  className="stat-icon"
                  whileHover={{ 
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                  style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                >
                  <FaBriefcase />
                </motion.div>
                <p><strong>2</strong> {t('about.iecd.tunisia.projects')}</p>
              </motion.div>
              <motion.div 
                className="tunisia-stat-item"
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 20px rgba(15, 76, 129, 0.1)',
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                <motion.div 
                  className="stat-icon"
                  whileHover={{ 
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                  style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                >
                  <FaHandshake />
                </motion.div>
                <p><strong>4</strong> {t('about.iecd.tunisia.localPartners')}<br /><strong>15</strong> {t('about.iecd.tunisia.businessPartners')}</p>
              </motion.div>
              <motion.div 
                className="tunisia-stat-item"
                whileHover={{ 
                  y: -5, 
                  boxShadow: '0 10px 20px rgba(15, 76, 129, 0.1)',
                  transition: { type: "spring", stiffness: 300 }
                }}
              >
                <motion.div 
                  className="stat-icon"
                  whileHover={{ 
                    rotate: 360,
                    transition: { duration: 0.5 }
                  }}
                  style={{ backgroundColor: "#ffd166", color: "#0f4c81" }}
                >
                  <FaBuilding />
                </motion.div>
                <p><strong>3</strong> {t('about.iecd.tunisia.trainingCenters')}</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Team Section 
      <motion.section 
        ref={teamRef}
        initial="hidden"
        animate={teamInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="team-section section"
      >
        <div className="container">
          <motion.div variants={itemVariants} className="section-header">
            <div className="section-icon-container">
              <FaUsers className="section-icon" />
            </div>
            <h2 className="section-title">{t('about.team.title')}</h2>
            <p className="section-subtitle">{t('about.team.subtitle')}</p>
          </motion.div>

          <motion.div variants={itemVariants} className="team-categories">
            <button 
              className={`team-category-btn ${activeTeamCategory === 'all' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('all'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.all')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'management' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('management'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.management')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'coach' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('coach'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.coach')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'pedagogique' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('pedagogique'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.pedagogique')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'entreprise' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('entreprise'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.entreprise')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'psycho' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('psycho'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.psycho')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'administration' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('administration'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.administration')}
            </button>
            <button 
              className={`team-category-btn ${activeTeamCategory === 'communication' ? 'active' : ''}`}
              onClick={() => {setActiveTeamCategory('communication'); setActiveTeamMemberIndex(0); setRefreshKey(prevKey => prevKey + 1);}}
            >
              {t('about.team.categories.communication')}
            </button>
          </motion.div>

          <motion.div variants={fadeInVariants} className="team-carousel">
            <button className="carousel-arrow prev" onClick={prevTeamMember}>
              <span>&#10094;</span>
            </button>
            
            <div className="team-members-display">
              {getFilteredTeamMembers().map((member, index) => (
                <motion.div 
                  key={`${refreshKey}-${index}`}
                  initial={{ opacity: 0, x: isMobile ? 50 : 100 }}
                  animate={{ 
                    opacity: index === activeTeamMemberIndex ? 1 : 0,
                    x: index === activeTeamMemberIndex ? 0 : (isMobile ? 50 : 100),
                    display: index === activeTeamMemberIndex ? 'flex' : 'none'
                  }}
                  transition={{ duration: isMobile ? 0.3 : 0.5 }}
                  className="team-member-card"
                >
                  <div className="team-member-image">
                    <img 
                      src={getTeamMemberImageUrl(member, index)}
                      alt={member.name} 
                      loading="lazy"
                      className="team-member-portrait"
                      onError={(e) => {
                        const memberName = member.name;
                        // Vérifier si c'est Fatma et réessayer avec l'URL Cloudinary directe
                        if (memberName === "فاطمة ممني" || memberName.includes("فاطمة")) {
                          // Utiliser directement l'ID Cloudinary pour Fatma
                          e.target.src = "https://res.cloudinary.com/dozvy3xru/image/upload/c_scale,w_400,q_100,f_auto/équipe/wezcmveqi2tcrgf3ju92";
                          // Définir un nouveau gestionnaire d'erreur au cas où cela échouerait aussi
                          e.target.onerror = () => {
                            e.target.onerror = null;
                            e.target.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                            e.target.classList.add("image-error");
                          };
                        } else {
                          // Comportement par défaut pour les autres membres
                          e.target.onerror = null;
                          e.target.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                          e.target.classList.add("image-error");
                        }
                      }}
                    />
                  </div>
                  <div className="team-member-info">
                    <h3 className="team-member-name">{member.name}</h3>
                    <p className="team-member-role">{member.role}</p>
                    <p className="team-member-description">{member.bio || member.description}</p>
                    {member.social && (
                      <div className="team-member-social">
                        {Object.entries(member.social).map(([platform, url]) => (
                          <a 
                            key={platform} 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label={platform}
                          >
                            {getSocialIcon(platform)}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
            
            <button className="carousel-arrow next" onClick={nextTeamMember}>
              <span>&#10095;</span>
            </button>
            
            <div className="carousel-dots">
              {getFilteredTeamMembers().map((_, index) => (
                <span 
                  key={index} 
                  className={`dot ${index === activeTeamMemberIndex ? 'active' : ''}`}
                  onClick={() => setActiveTeamMemberIndex(index)}
                ></span>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>
      */}
      {/* Partners Section */}
      <section 
        ref={partnersRef}
        className="partners-section section"
      >
        <div className="container">
          <div className="section-header">
            <div className="section-icon-container">
              <FaHandshake className="section-icon" />
            </div>
            <h2 className="section-title">{t('about.partners.title')}</h2>
            <p className="section-subtitle">{t('about.partners.subtitle')}</p>
            <p className="partners-description">{t('about.partners.description')}</p>
          </div>

          <div className="partner-categories">
            <button 
              className={`filter-button ${activePartnerCategory === 'institutional' ? 'active' : ''}`}
              onClick={() => setActivePartnerCategory('institutional')}
            >
              {t('about.partners.categories.institutional')}
            </button>
            <button 
              className={`filter-button ${activePartnerCategory === 'enterprises' ? 'active' : ''}`}
              onClick={() => setActivePartnerCategory('enterprises')}
            >
              {t('about.partners.categories.enterprises')}
            </button>
            <button 
              className={`filter-button ${activePartnerCategory === 'ngos' ? 'active' : ''}`}
              onClick={() => setActivePartnerCategory('ngos')}
            >
              {t('about.partners.categories.ngos')}
            </button>
          </div>

          <div className="partners-grid">
            {getFilteredPartners().map((partner) => (
              <div
                key={partner.id}
                className="partner-card"
              >
                <div className="partner-logo-container">
                  <img 
                    src={partner.logo} 
                    alt={partner.name} 
                    className="partner-logo"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
                      e.target.classList.add("logo-error");
                    }}
                  />
                </div>
                <div className="partner-info">
                  <h3>{partner.name}</h3>
                  <p>{partner.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="become-partner">
            <a 
              href="./contact"
              className="become-partner-btn"
            >
              {t('about.partners.becomePartner')} <FaArrowRight className="btn-icon" />
            </a>
          </div>
        </div>
      </section>
      
      {/* Values Section */}
      <motion.section 
        ref={valuesRef}
        initial="hidden"
        animate={valuesInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="values-section section"
      >
        <div className="container">
          <motion.div 
            variants={itemVariants} 
            className="section-header"
            whileInView={{ 
              y: [10, 0], 
              opacity: [0, 1] 
            }}
            transition={{ duration: 0.8 }}
          >
            <div className="section-icon-container">
              <FaBookOpen className="section-icon" />
            </div>
            <h2 className="section-title">{t('about.values.title')}</h2>
            <p className="section-subtitle">{t('about.values.subtitle')}</p>
          </motion.div>

          <motion.div 
            variants={containerVariants} 
            className="values-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {/* Utilisation des traductions pour les valeurs */}
            {t('about.values.values', { returnObjects: true }).map((value, index) => (
              <motion.div 
                key={index}
                className="value-card"
                variants={popUpVariants}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ 
                  y: -15, 
                  boxShadow: '0 15px 30px rgba(15, 76, 129, 0.15)',
                  transition: { type: "spring", stiffness: 300 }
                }}
                transition={{
                  delay: index * 0.1,
                  duration: 0.5,
                  ease: "easeOut"
                }}
              >
                <motion.div 
                  className="value-icon-container"
                  whileHover={{ 
                    scale: 1.1,
                    backgroundColor: "#0f4c81",
                    transition: { duration: 0.3 }
                  }}
                >
                  <motion.div
                    className="value-icon"
                    whileHover={{ 
                      color: "#ffd166",
                      rotate: 360,
                      transition: { duration: 0.5 }
                    }}
                  >
                    {getValueIcon(value.icon)}
                  </motion.div>
                </motion.div>
                <motion.h3 
                  className="value-title"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  {value.title}
                </motion.h3>
                <motion.p 
                  className="value-description"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  {value.description}
                </motion.p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default About;
